'use client';

import { useCallback, useRef } from 'react';
import { useFetch } from '@/app/hooks';
import { listInstalledSkills, listMySkills } from '../apis';
import type { ClawInstalledSkill, ClawSkill } from '../types';

/** skill 来源分组：实例已安装 vs 平台目录。 */
export type ClawSkillGroup = 'installed' | 'platform';

/** 技能选择器列表项：把两类 skill 收敛成统一的展示与插入字段。 */
export interface ClawSkillItem {
  /** 列表唯一标识：`${group}:${name|id}`，仅用于 React key / command 反查，不参与发送。 */
  id: string;
  /** 插入 chip 用的展示名（发送时取 `/${label}`）：installed 用 name，platform 优先 title。 */
  label: string;
  /** skill 名称（运行时标识）。 */
  name: string;
  /** 来源分组，用于列表分段标题。 */
  group: ClawSkillGroup;
  /** 描述（installed 才有），暂未在列表展示，保留给后续。 */
  description?: string;
}

const SKILL_PAGE_SIZE = 20;

const toPlatformItem = (skill: ClawSkill): ClawSkillItem | null => {
  const id = skill.id?.trim();
  const name = skill.name?.trim() ?? '';
  const label = skill.title?.trim() || name;
  if (!id || !label) return null;
  return { id: `platform:${id}`, label, name, group: 'platform' };
};

const toInstalledItem = (skill: ClawInstalledSkill): ClawSkillItem | null => {
  const name = skill.name?.trim();
  if (!name) return null;
  return {
    id: `installed:${name}`,
    label: name,
    name,
    group: 'installed',
    description: skill.description?.trim() || undefined,
  };
};

const matchKeyword = (item: ClawSkillItem, keyword: string): boolean => {
  if (!keyword) return true;
  const lower = keyword.toLowerCase();
  return item.label.toLowerCase().includes(lower) || item.name.toLowerCase().includes(lower);
};

interface UseClawSkillSearchOptions {
  /** 当前 claw 实例 id；缺省时只展示平台 skill（installed 需要 claw_id 直连 picoclaw）。 */
  activeClawId?: string;
  /**
   * 允许唤起的 skill 来源分组，缺省两类都取。
   * 欢迎页传 ['installed', 'platform']，可唤起平台技能引导安装；
   * 活跃 session 传 ['installed']，只允许唤起实例上已安装、真正能跑的 skill。
   */
  enabledGroups?: ClawSkillGroup[];
}

/**
 * `/` 技能选择器的数据源，合并两类 skill：
 * - installed（clawsListInstalledSkills）：按 claw_id 直连实例，结果按 claw_id 缓存，本地 keyword 过滤；
 * - platform（clawsListMySkills）：平台目录，远端 keyword 过滤；
 * 两者并发取，installed 排在前（实例上真正能跑的优先）。
 * 列表整体 500ms 防抖，与 @ mention 搜索一致。
 * enabledGroups 控制纳入哪些来源（活跃 session 只传 ['installed']）。
 */
export const useClawSkillSearch = ({
  activeClawId,
  enabledGroups = ['installed', 'platform'],
}: UseClawSkillSearchOptions = {}) => {
  const includeInstalled = enabledGroups.includes('installed');
  const includePlatform = enabledGroups.includes('platform');
  // installed 接口不支持 keyword，且同一实例结果稳定：按 claw_id 缓存，避免每次输入都重复直连 picoclaw。
  const installedCacheRef = useRef<{ clawId: string; items: ClawSkillItem[] } | null>(null);

  const fetchInstalledItems = useCallback(async (clawId: string): Promise<ClawSkillItem[]> => {
    if (installedCacheRef.current?.clawId === clawId) {
      return installedCacheRef.current.items;
    }
    const { data } = await listInstalledSkills(clawId);
    // picoclaw 不可达（reachable=false）时 skills 一般为空，静默降级为只剩平台 skill。
    const items = (data?.skills ?? []).map(toInstalledItem).filter((item): item is ClawSkillItem => item !== null);
    installedCacheRef.current = { clawId, items };
    return items;
  }, []);

  const { runAsync: fetchSkills } = useFetch(
    async (keyword: string, clawId?: string) => {
      const [installedItems, platformReply] = await Promise.all([
        includeInstalled && clawId ? fetchInstalledItems(clawId) : Promise.resolve<ClawSkillItem[]>([]),
        includePlatform
          ? listMySkills({ keyword: keyword || undefined, 'pager.pagesz': SKILL_PAGE_SIZE })
          : Promise.resolve(null),
      ]);

      const platformItems = (platformReply?.data?.skills ?? [])
        .map(toPlatformItem)
        .filter((item): item is ClawSkillItem => item !== null);

      // installed 走本地过滤；platform 已远端按 keyword 过滤，无需再过滤。
      const filteredInstalled = installedItems.filter((item) => matchKeyword(item, keyword));

      return [...filteredInstalled, ...platformItems];
    },
    {
      debounceWait: 500,
      manual: true,
    },
  );

  const searchSkillItems = useCallback(
    async (query: string): Promise<ClawSkillItem[]> => {
      const result = await fetchSkills(query.trim(), activeClawId);
      return result ?? [];
    },
    [fetchSkills, activeClawId],
  );

  return { searchSkillItems };
};
